import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '@/lib/axios';
import { 
  ArrowLeft, User, Building2, Phone, Mail, MapPin, Save, 
  AlertCircle, CheckCircle, Eye, Loader2, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import { useBranding } from '../lib/BrandingContext';
import AdvocateStyleModal from '@/components/AdvocateStyleModal';

const AdvocateProfileSettings = () => {
  const navigate = useNavigate();
  const { branding } = useBranding();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [profile, setProfile] = useState({
    advocate_name: '',
    advocate_qualifications: '',
    firm_name: '',
    office_address: '',
    office_phone: '',
    residence_address: '',
    residence_phone: '',
    cell_numbers: '',
    email: '',
    enrollment_no: '',
    default_place: 'Hyderabad'
  });
  const [previewData, setPreviewData] = useState(null);
  const [tenantId, setTenantId] = useState('');

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/tenant/advocate-profile`
      );
      if (response.data.success && response.data.profile) {
        // Map the API response to our form fields
        const p = response.data.profile;
        setTenantId(p.tenant_id || '');
        setProfile({
          advocate_name: p.advocate_name || '',
          advocate_qualifications: p.advocate_qualifications || '',
          firm_name: p.firm_name || '',
          office_address: p.office_address || '',
          office_phone: p.office_phone || '',
          residence_address: p.residence_address || '',
          residence_phone: p.residence_phone || '',
          cell_numbers: p.cell_numbers || '',
          email: p.email || '',
          enrollment_no: p.enrollment_no || '',
          default_place: p.default_place || 'Hyderabad'
        });
      }
    } catch (err) {
      console.error('Error fetching profile:', err);
      if (err.response?.status === 401) {
        return;
      }
      toast.error('Failed to load profile settings');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setProfile(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/tenant/advocate-profile`,
        profile
      );
      if (response.data.success) {
        toast.success('Profile updated successfully');
        // Update profile with response data
        if (response.data.profile) {
          const p = response.data.profile;
          setProfile({
            advocate_name: p.advocate_name || '',
            advocate_qualifications: p.advocate_qualifications || '',
            firm_name: p.firm_name || '',
            office_address: p.office_address || '',
            office_phone: p.office_phone || '',
            residence_address: p.residence_address || '',
            residence_phone: p.residence_phone || '',
            cell_numbers: p.cell_numbers || '',
            email: p.email || '',
            enrollment_no: p.enrollment_no || '',
            default_place: p.default_place || 'Hyderabad'
          });
        }
      }
    } catch (err) {
      console.error('Error saving profile:', err);
      if (err.response?.status === 401) {
        return;
      }
      toast.error('Failed to save profile settings');
    } finally {
      setSaving(false);
    }
  };

  const fetchPreview = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/tenant/header-preview`
      );
      if (response.data.success) {
        setPreviewData(response.data);
        setShowPreview(true);
      }
    } catch (err) {
      console.error('Error fetching preview:', err);
      if (err.response?.status === 401) {
        return;
      }
      toast.error('Failed to load preview');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading profile settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Advocate Profile Settings</h1>
                <p className="text-sm text-gray-500">
                  Configure your header information for document generation
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={fetchPreview}
                className="flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                <Eye className="w-4 h-4" />
                <span>Preview</span>
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center space-x-2 px-4 py-2 text-sm text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                <span>{saving ? 'Saving...' : 'Save Changes'}</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto py-8 px-6">
        {/* Info Banner */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-start space-x-3">
            <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <h3 className="text-sm font-medium text-blue-900">About Your Profile</h3>
              <p className="text-sm text-blue-700 mt-1">
                This information will appear in the header of all legal opinion documents generated by 
                <strong> all users in your organization</strong>, regardless of which bank format is selected.
                The bank format controls the document structure, while your profile controls your identity.
              </p>
              {tenantId && (
                <p className="text-xs text-blue-600 mt-2">
                  Organization ID: <code className="bg-blue-100 px-1 py-0.5 rounded">{tenantId}</code>
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Form */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          {/* Personal Details Section */}
          <div className="border-b border-gray-200 bg-gray-50 px-6 py-4">
            <div className="flex items-center space-x-2">
              <User className="w-5 h-5 text-gray-600" />
              <h2 className="text-lg font-semibold text-gray-900">Personal Details</h2>
            </div>
          </div>
          <div className="p-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Advocate Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={profile.advocate_name}
                  onChange={(e) => handleChange('advocate_name', e.target.value)}
                  placeholder="e.g., G VAMSHI KRISHNA"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  This will appear at the top of your documents
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Qualifications
                </label>
                <input
                  type="text"
                  value={profile.advocate_qualifications}
                  onChange={(e) => handleChange('advocate_qualifications', e.target.value)}
                  placeholder="e.g., B. Tech., MBA., LL.M."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Firm Name (Optional)
                </label>
                <input
                  type="text"
                  value={profile.firm_name}
                  onChange={(e) => handleChange('firm_name', e.target.value)}
                  placeholder="Leave empty if individual practice"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Bar Council Enrollment No.
                </label>
                <input
                  type="text"
                  value={profile.enrollment_no}
                  onChange={(e) => handleChange('enrollment_no', e.target.value)}
                  placeholder="e.g., TS/1234/2012"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Office Details Section */}
          <div className="border-t border-b border-gray-200 bg-gray-50 px-6 py-4">
            <div className="flex items-center space-x-2">
              <Building2 className="w-5 h-5 text-gray-600" />
              <h2 className="text-lg font-semibold text-gray-900">Office Details</h2>
            </div>
          </div>
          <div className="p-6 space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Office Address
              </label>
              <textarea
                value={profile.office_address}
                onChange={(e) => handleChange('office_address', e.target.value)}
                placeholder="e.g., 4-1-875 to 877/1, 401, 4th Floor, RDB Blue Hope, Abids, Hyderabad - 500 001"
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Office Phone
                </label>
                <input
                  type="text"
                  value={profile.office_phone}
                  onChange={(e) => handleChange('office_phone', e.target.value)}
                  placeholder="e.g., 24613543"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={profile.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  placeholder="advocate@example.com"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Residence Details Section */}
          <div className="border-t border-b border-gray-200 bg-gray-50 px-6 py-4">
            <div className="flex items-center space-x-2">
              <MapPin className="w-5 h-5 text-gray-600" />
              <h2 className="text-lg font-semibold text-gray-900">Residence Details (Optional)</h2>
            </div>
          </div>
          <div className="p-6 space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Residence Address
              </label>
              <textarea
                value={profile.residence_address}
                onChange={(e) => handleChange('residence_address', e.target.value)}
                placeholder="e.g., 8-3-833/59, Phase I, Kamalapuri Colony, Hyderabad – 500 073"
                rows={2}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Residence Phone
                </label>
                <input
                  type="text"
                  value={profile.residence_phone}
                  onChange={(e) => handleChange('residence_phone', e.target.value)}
                  placeholder="e.g., 23547798"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Cell / Mobile Numbers
                </label>
                <input
                  type="text"
                  value={profile.cell_numbers}
                  onChange={(e) => handleChange('cell_numbers', e.target.value)}
                  placeholder="e.g., 09704055589"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Default Settings Section */}
          <div className="border-t border-b border-gray-200 bg-gray-50 px-6 py-4">
            <div className="flex items-center space-x-2">
              <MapPin className="w-5 h-5 text-gray-600" />
              <h2 className="text-lg font-semibold text-gray-900">Default Settings</h2>
            </div>
          </div>
          <div className="p-6">
            <div className="max-w-md">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Default Place (for documents)
              </label>
              <input
                type="text"
                value={profile.default_place}
                onChange={(e) => handleChange('default_place', e.target.value)}
                placeholder="e.g., Hyderabad"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                This place will be used as the default in generated documents
              </p>
            </div>
          </div>
        </div>

        {/* Drafting DNA Studio Section */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mt-6">
          <div className="border-b border-gray-200 bg-gradient-to-r from-purple-50 to-indigo-50 px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Sparkles className="w-5 h-5 text-purple-600" />
                <h2 className="text-lg font-semibold text-gray-900">Drafting DNA Studio</h2>
              </div>
              <span className="text-xs font-medium text-purple-600 bg-purple-100 px-2 py-1 rounded-full">
                NEW
              </span>
            </div>
            <p className="text-sm text-gray-600 mt-1">
              Create a Drafting DNA from your proven work.
            </p>
          </div>
          <div className="p-6">
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-purple-800">
                <strong>How it works:</strong> Upload your approved opinions, compile a Drafting DNA,
                and use the resulting drafting standard in generation — without changing facts,
                risks, legal conclusions, or lender policy outputs.
              </p>
            </div>
            <AdvocateStyleModal
              trigger={
                <button data-testid="advocate-style-btn" className="w-full flex items-center justify-center space-x-2 px-4 py-3 text-sm text-purple-700 bg-purple-100 hover:bg-purple-200 rounded-lg transition-colors border border-purple-300">
                  <Sparkles className="w-5 h-5" />
                  <span>Create or Update Drafting DNA</span>
                </button>
              }
              onStyleChange={() => toast.success('Drafting DNA updated!')}
            />
          </div>
        </div>

        {/* Save Button (Mobile) */}
        <div className="mt-6 md:hidden">
          <button
            onClick={handleSave}
            disabled={saving}
            className="w-full flex items-center justify-center space-x-2 px-4 py-3 text-sm text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            <span>{saving ? 'Saving...' : 'Save Changes'}</span>
          </button>
        </div>
      </main>

      {/* Preview Modal */}
      {showPreview && previewData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Header Preview</h3>
              <button
                onClick={() => setShowPreview(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>
            <div className="p-6">
              <p className="text-sm text-gray-500 mb-4">
                This is how your header will appear in generated documents:
              </p>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 font-serif">
                {previewData.preview_lines?.map((line, idx) => (
                  <p key={idx} className={`${idx === 0 ? 'font-bold text-lg' : 'text-sm'} text-gray-900`}>
                    {line}
                  </p>
                ))}
              </div>
              <div className="mt-4 flex items-center text-green-600">
                <CheckCircle className="w-4 h-4 mr-2" />
                <span className="text-sm">This header will be used for all bank formats</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdvocateProfileSettings;
